/**
 * 충전금 잔액 카드 + 상세 시트 — 충전(/m/account/deposit) · 사용내역(/m/cash/list)이 같이 쓴다.
 * 사용내역은 카드 없이 시트만 쓴다(render 를 부르지 않고, $rows 에 [data-sheet] 버튼을 직접 둔다).
 *
 * 카드: 사용가능 충전금 아래로 '전체 − 나갈 금액(수주 홀드 · 출금 신청 중) / + 받을 금액'.
 * 시트: 금액 줄을 누르면 그 합계가 어디서 나왔는지를 월별 카드로 보여준다 — 수주 홀드·받을 금액은
 * 어느 화면에서든 같은 탭 시트(openHoldSheet) 하나고, 출금 신청만 단일 시트다.
 * 숫자는 CashQueryService::balance 가 내려준 것을 그대로 쓴다 (소유자 기준).
 *
 * 페이지 모듈이 import 해서 new 한다 (Pages/Mobile/Diary/* 와 같은 하위 모듈 — $j 에 등록하지 않는다).
 *   const card = new CashBalance({ $usable, $rows, balance });  card.render();
 * 스타일은 pages/_cash-deposit.scss 의 .cd-* (카드 줄 · 시트).
 */
import Util from '../../../Common/Util';

const SHEET_SIZE = 20;          // 상세 시트가 한 페이지에 받는 줄 수 — 무한스크롤로 이만큼씩 잇는다

// 잔액 카드에서 눌러 여는 상세 시트 — 수주 홀드·받을 금액은 탭 시트(openHoldSheet)의 탭이라 안내문만 갖는다
const SHEETS = {
    soo: { note: '수주 수수료로 나갈 금액이에요. 작업이 끝나면 빠져나가요. (작업일 기준)' },
    bal: { note: '발주 수수료로 받을 금액이에요. 작업이 끝나면 들어와요. (작업일 기준)' },
    request: {
        title: '출금 신청 내역', sum: '출금 신청 합계',
        note: '평일 09:00~17:00에 처리돼요 (3시간 이내 · 주말/공휴일 제외). 수주할 충전금이 모자라면 출금 신청이 자동으로 취소될 수 있어요.',
    },
};

const IC_CHEV = '<svg class="cd-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>';
// 눌리는 줄의 금액 칩 꺾쇠 — 칩(회색 채움 + 꺾쇠) 모양이 버튼 간판 노릇을 한다
const IC_CHEV_SM = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>';

export default class CashBalance {
    static IC_CHEV = IC_CHEV;

    static won(n) {
        return Util.formatNumber(n) + '원';
    }

    static esc(str) {
        return $('<i>').text(str).html();
    }

    /** 최신순 목록을 달마다 묶는다 — 같은 달은 이어서 온다. @returns {{ym:string, rows:object[]}[]} */
    static groupByMonth(rows, dateKey) {
        const groups = [];
        rows.forEach((row) => {
            const ym = dayjs(row[dateKey]).format('YYYY-MM');
            const last = groups[groups.length - 1];
            if (last && last.ym === ym) last.rows.push(row);
            else groups.push({ ym, rows: [row] });
        });
        return groups;
    }

    /**
     * 리뉴얼 규격의 바텀시트를 연다 (제목 왼쪽 15px · 닫기 44px · 본문 여백 — _cash-deposit.scss 의 .cd-bs).
     * 공용 시트의 모양은 그대로 두고 이 시트에만 클래스를 붙였다 뗀다 — 같은 화면에서 여는 다른 시트
     * (오더 상세 등)는 공용 모양 그대로다.
     * @param {string} title
     * @param {(body: JQuery) => void} fill 본문을 채운다
     * @param {{full?: boolean, onClose?: () => void}} [opt]
     *   full    — true(기본)면 화면의 95% 높이로 연다(줄 수와 무관하게 같은 높이, 길면 본문이 스크롤된다).
     *             false 면 내용만큼만 올라온다 — 조회 설정처럼 짧은 시트
     *   onClose — 닫힐 때(다른 시트로 바뀔 때 포함) 불린다. 옮겨 온 노드를 제자리로 되돌리는 자리
     */
    static openSheet(title, fill, { full = true, onClose } = {}) {
        $j.Ui.BS.open({
            backClose: true,
            fixedHeight: full,
            target: {
                init() {
                    const $body = $('#' + this.id);
                    this.$popup = $body.closest('.bottomsheet-popup').addClass('cd-bs');
                    fill($body);
                },
                destroy() {
                    if (onClose) onClose();
                    if (this.$popup) this.$popup.removeClass('cd-bs');
                },
            },
        });
        $j.Ui.BS.setTitle(title);
    }

    /**
     * @param {{$usable: JQuery, $rows: JQuery, balance: object}} opt
     *   balance — { total, soo_hold, request, bal_hold } (서버 CashQueryService::balance)
     */
    constructor({ $usable, $rows, balance }) {
        this.$usable = $usable;
        this.$rows = $rows;
        this.set(balance);
        // 수주 홀드·받을 금액은 어느 화면에서 열든 같은 탭 시트(해당 탭이 선택된 채) — 'hold' 는 내역탭 묶음 칩.
        // 출금 신청 중만 홀드가 아니라서 단일 시트다
        this.$rows.on('click', '[data-sheet]', (e) => {
            const key = $(e.currentTarget).attr('data-sheet');
            if (key === 'request') this.openSheet(key);
            else this.openHoldSheet(key === 'bal' ? 'bal' : 'soo');
        });
    }

    set(balance) {
        this.balance = {
            total: Util.int(balance.total), soo_hold: Util.int(balance.soo_hold),
            request: Util.int(balance.request), bal_hold: Util.int(balance.bal_hold),
        };
    }

    /** 0원인 줄은 그리지 않는다. 나갈 금액이 없으면 '전체 = 사용가능'이라 전체 충전금도 뺀다 */
    render() {
        const b = this.balance;
        const out = b.soo_hold + b.request;
        this.$usable.text(Util.formatNumber(b.total - out));

        // 내역이 있는 줄만 눌러서 시트를 연다 — 그 줄은 금액 자체가 칩(회색 채움 + 꺾쇠).
        // 금액은 모든 줄 오른쪽 끝으로 맞는다
        const row = (label, val, cls, sheet) => {
            const inner = `<span class="cd-k">${label}</span>`
                + `<span class="cd-v${sheet ? ' cd-v--chip' : ''}">${CashBalance.won(val)}${sheet ? IC_CHEV_SM : ''}</span>`;
            return sheet
                ? `<button type="button" class="cd-row is-link ${cls}" data-sheet="${sheet}">${inner}</button>`
                : `<div class="cd-row ${cls}">${inner}</div>`;
        };

        let html = '';
        if (out > 0) {
            html += '<div class="cd-rows">'
                + row('<i></i>전체 충전금', b.total, '')
                + row('<i>−</i>나갈 금액', out, '')
                + (b.soo_hold > 0 ? row('수주 홀드', b.soo_hold, 'is-sub', 'soo') : '')
                + (b.request > 0 ? row('출금 신청 중', b.request, 'is-sub', 'request') : '')
                + '</div>';
        }
        // 받을 금액은 계산식 밖이다 — 나갈 금액 아래에 올 때만 점선으로 가른다
        if (b.bal_hold > 0) {
            html += `<div class="cd-rows${out > 0 ? ' is-in' : ''}">` + row('<i>+</i>받을 금액', b.bal_hold, '', 'bal') + '</div>';
        }
        this.$rows.html(html);
    }

    /* ───────────────── 상세 시트 ───────────────── */

    /**
     * 홀드 시트 — 수주 홀드 · 받을 금액을 탭 둘로 가른다. 내역탭 「홀드」 칩과 충전탭 잔액 카드 줄이
     * 같은 시트를 쓴다(누른 쪽 탭이 선택된 채 열림).
     * 탭 머리에 합계를 크게 싣고, 탭 아래는 안내 + 월별 목록. 금액에 부호는 안 붙인다 — 탭 이름과 안내문이
     * 방향(나갈/받을)을 말하고, 탭 하나는 한 방향뿐이라 부호가 할 일이 없다(잔액 카드의 '+ 받을 금액'은 계산식이라 다르다)
     */
    openHoldSheet(key = 'soo') {
        // 제목 옆 합계총액 — 수주 홀드 + 받을 금액 (칩에 실은 수와 같은 셈)
        const total = this.balance.soo_hold + this.balance.bal_hold;
        CashBalance.openSheet(`홀드 금액<b class="cd-bs-amt">총 ${CashBalance.won(total)}</b>`, ($body) => {
            const tab = (k, label, val) =>
                `<button type="button" class="cd-tab" data-k="${k}">`
                + `<span>${label}</span><b>${CashBalance.won(val)}</b></button>`;
            $body.html(
                `<div class="cd-sheet cd-sheet--tight">`
                + `<div class="cd-tabs">${tab('soo', '수주 홀드', this.balance.soo_hold)}${tab('bal', '받을 금액', this.balance.bal_hold)}</div>`
                + `<p class="cd-sheet-note"></p>`
                + `<div class="cd-sheet-list"></div></div>`
            );
            const show = (k) => {
                $body.find('.cd-tab').each((i, el) => {
                    const on = $(el).attr('data-k') === k;
                    $(el).toggleClass('is-on', on).attr('aria-pressed', on ? 'true' : 'false');
                });
                $body.find('.cd-sheet-note').text(SHEETS[k].note);
                this._loadSheet(k, $body.find('.cd-sheet-list').html('<p class="cd-sheet-msg">불러오는 중…</p>'));
            };
            $body.on('click', '.cd-tab', (e) => show($(e.currentTarget).attr('data-k')));
            show(key);
        });
    }

    /** @param {'soo'|'bal'|'request'} key */
    openSheet(key) {
        const conf = SHEETS[key];
        const total = { soo: this.balance.soo_hold, bal: this.balance.bal_hold, request: this.balance.request }[key];

        CashBalance.openSheet(conf.title, ($body) => {
            $body.html(
                `<div class="cd-sheet">`
                + `<div class="cd-sheet-sum"><span>${conf.sum}</span><b>${CashBalance.won(total)}</b></div>`
                + `<p class="cd-sheet-note">${conf.note}</p>`
                + `<div class="cd-sheet-list"><p class="cd-sheet-msg">불러오는 중…</p></div></div>`
            );
            this._loadSheet(key, $body.find('.cd-sheet-list'));
        });
    }

    /**
     * 시트 목록 — 무한스크롤. SHEET_SIZE 씩 받아 월 카드로 다시 묶는다(페이지 경계에서 달이 이어지면 같은 카드).
     * 끝 감지는 목록 끝의 '불러오는 중…' 줄에 공용 $j.Ui.InfiniteScroll 을 붙인다 — 시트의 스크롤 부모를 찾을 필요가 없다.
     * 탭 시트에서 탭을 바꾸면 같은 $list 에 다시 불린다 — 세대 번호(_sheetGen)로 이전 탭의 늦은 응답을 버리고 감시를 뗀다.
     * 달마다 카드 하나 — 충전 내역과 같은 머리 띠. 홀드는 작업일, 출금 신청은 신청일로 묶는다.
     * 달 합계는 적지 않는다 — 위에 전체 합계가 있다
     */
    _loadSheet(key, $list) {
        const isRequest = key === 'request';
        const gen = this._sheetGen = (this._sheetGen || 0) + 1;
        // total 은 첫 페이지 응답에만 실려 온다(서버가 뒤 페이지에선 집계를 생략) — 한 번 받아 둔다
        const state = { rows: [], total: null, busy: false, done: false };

        // 카드 자리와 끝 표시를 갈라 둔다 — 끝 표시는 같은 요소를 계속 감시하고, 더 받을 게 없으면 숨긴다(숨으면 교차 판정이 안 난다)
        $list.data('cdScroll')?.destroy();
        $list.html('<div class="cd-sheet-cards"><p class="cd-sheet-msg">불러오는 중…</p></div>'
            + '<p class="cd-sheet-msg cd-sheet-more" hidden>불러오는 중…</p>');
        const $cards = $list.find('.cd-sheet-cards'), $more = $list.find('.cd-sheet-more');
        const scroll = $j.Ui.InfiniteScroll.attach($more[0], { onLoad: () => load() });
        $list.data('cdScroll', scroll);
        // IntersectionObserver 미지원 — 끝 줄을 눌러서 받는다
        if (!scroll) $more.text('더 보기').on('click', () => load());

        const render = () => {
            if (!state.rows.length) { $cards.html('<p class="cd-sheet-msg">내역이 없어요.</p>'); $more.prop('hidden', true); return; }
            $cards.html(CashBalance.groupByMonth(state.rows, isRequest ? 'request_date' : 'work_day').map((g) =>
                `<div class="cd-sheet-card"><h3 class="cx-month">${dayjs(g.ym + '-01').format('YYYY년 M월')}</h3>`
                + g.rows.map((row) => (isRequest ? this._requestRowHtml(row) : this._holdRowHtml(row, key))).join('')
                + `</div>`
            ).join(''));
            $more.prop('hidden', state.done);
            // 감시를 다시 건다 — 새 줄을 붙여도 끝 표시가 그대로 화면 안이면 교차 변화가 없어 다음 쪽이 안 불린다
            if (scroll && !state.done) { scroll.pause(); scroll.resume(); }
        };

        const load = () => {
            if (state.busy || state.done) return;
            state.busy = true;
            const req = isRequest
                ? $.post('/m/ajax/account/list', { type: 2, status: 20, start: state.rows.length, length: SHEET_SIZE })
                : $.post('/m/ajax/member/hold/logs', { smode: key === 'bal' ? 1 : 2, start: state.rows.length, length: SHEET_SIZE });
            req.done((res) => {
                if (gen !== this._sheetGen) return;     // 그새 다른 탭을 열었다
                const rows = res.data || [];
                state.rows = state.rows.concat(rows);
                if (state.total === null && res.recordsTotal != null) state.total = Util.int(res.recordsTotal);
                state.done = rows.length < SHEET_SIZE || (state.total !== null && state.rows.length >= state.total);
                render();
            }).fail(() => {
                if (gen !== this._sheetGen) return;
                if (!state.rows.length) { $cards.html('<p class="cd-sheet-msg">불러오지 못했어요. 닫고 다시 열어 주세요.</p>'); $more.prop('hidden', true); return; }
                state.done = true;              // 뒤 페이지 실패 — 받은 데까지만 두고 더 부르지 않는다
                render();
            }).always(() => { state.busy = false; });
        };

        load();
    }

    /**
     * 홀드 한 줄 — 누르면 그 오더로 간다. 윗줄에 날짜와 금액, 아랫줄에 주소. 주소를 날짜·금액 사이 칸에 넣으면 좁은 칸에서 여러 줄로 접혀 표처럼 보인다
     */
    _holdRowHtml(row, key) {
        return `<a class="cd-sheet-row" href="/m/alim/redirect?ordernum=${Util.int(row.num)}">`
            + `<span class="cd-sheet-d">${dayjs(row.work_day).format('M.D')}</span>`
            + `<span class="cd-sheet-a">${CashBalance.won(Math.abs(Util.int(row.point)))}</span>${IC_CHEV}`
            + `<span class="cd-sheet-m">${CashBalance.esc(Util.nvl(row.site_address, ''))}</span></a>`;
    }

    _requestRowHtml(row) {
        const bank = Util.decodeMemberBank(row.r_bank);
        const account = CashBalance.esc([bank.bank, bank.actholder].filter(Boolean).join(' '));
        return `<div class="cd-sheet-row">`
            + `<span class="cd-sheet-d">${dayjs(row.request_date).format('M.D')}</span>`
            + `<span class="cd-sheet-a">${CashBalance.won(Util.int(row.amount))}</span><span class="cx-badge">출금신청</span>`
            + `<span class="cd-sheet-m">${account || '출금 신청'}</span></div>`;
    }
}
